import { Link, useParams } from "react-router-dom";
import { CONDITION_CARDS } from "../data/content";
import LeadForm from "../components/site/LeadForm";
import WhatsAppButton from "../components/site/WhatsAppButton";
import { ArrowRight, Activity } from "lucide-react";

export function ConditionsList() {
  return (
    <div className="container-wide py-16">
      <div className="max-w-3xl">
        <div className="eyebrow mb-3">Conditions we help manage</div>
        <h1 className="font-heading text-4xl md:text-6xl text-brand-ink leading-[1.05]">Not sure where you fit? Start here.</h1>
        <p className="text-brand-ink2 mt-5">Pick the closest match — we'll take it from there with an assessment.</p>
      </div>
      <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {CONDITION_CARDS.map((c) => (
          <Link key={c.slug} to={`/conditions/${c.slug}`} className="card-soft p-6 hover:border-brand-olive/30" data-testid={`cond-${c.slug}`}>
            <div className="w-10 h-10 rounded-xl bg-brand-cream text-brand-olive flex items-center justify-center mb-3"><Activity size={18}/></div>
            <div className="font-heading text-lg text-brand-ink">{c.title}</div>
            <div className="text-sm text-brand-ink2 mt-1">{c.short}</div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function ConditionDetail() {
  const { slug } = useParams();
  const cond = CONDITION_CARDS.find((c) => c.slug === slug);
  if (!cond) return <div className="container-wide py-24"><h1 className="font-heading text-3xl">Condition not found</h1><Link to="/conditions" className="btn-outline mt-6">Back</Link></div>;

  const waMsg = `Hello Physio Hyve, I'd like to discuss ${cond.title.toLowerCase()}.`;
  return (
    <div className="container-wide py-16 grid md:grid-cols-3 gap-10">
      <div className="md:col-span-2 space-y-6">
        <div className="eyebrow">Condition</div>
        <h1 className="font-heading text-4xl md:text-6xl text-brand-ink leading-[1.05]">{cond.title}</h1>
        <p className="text-brand-ink2">{cond.short} — assessment and clinical reasoning come first, then a personalised rehabilitation plan.</p>
        <div className="prose-hyve">
          <h2>What patients typically experience</h2>
          <p>Symptoms often include pain during specific movements, stiffness after rest, reduced strength or a loss of confidence in your body. Every case is different — that's exactly why we begin with a proper assessment.</p>
          <h2>What we look at during assessment</h2>
          <ul>
            <li>History, mechanism and behaviour of symptoms</li>
            <li>Range of motion, strength and control</li>
            <li>Functional testing relevant to your goals</li>
            <li>Contributing factors — posture, loading, previous injuries</li>
          </ul>
          <h2>How Physio Hyve can help</h2>
          <p>We build a rehabilitation plan around your goals, combining appropriate hands-on techniques with progressive exercise-based rehab. We'll be transparent about what to expect, and honest if we think surgical or medical review is warranted.</p>
        </div>
        <div className="flex gap-3 flex-wrap">
          <Link to="/book" className="btn-primary">Book an Assessment <ArrowRight size={16}/></Link>
          <WhatsAppButton message={waMsg}>WhatsApp about {cond.title}</WhatsAppButton>
        </div>
      </div>
      <aside className="md:sticky md:top-24 self-start">
        <LeadForm category={slug} sourcePage={`condition-${slug}`} defaultConcern={`I have ${cond.title.toLowerCase()}`} />
      </aside>
    </div>
  );
}
